import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useModal} from 'common/hooks/useModal';
import {ITestFileResult} from 'types/TestFileResult';
import api from 'api';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import {DialogActions, DialogContent} from '@mui/material';
import Typography from '@mui/material/Typography';
import {useGetApiCallback} from 'common/hooks/useGetApiCallback';
import Box from '@mui/material/Box';
import {DirectoryContentsList} from './common/DirectoryContentsList';
import {IDiplomatFile} from 'types/DiplomatFile';
import {transportDataGetterContext} from 'features/layout/CommonPage/panels/TransportPanel/components/common/transportDataGetterContext';
import {useIsTestPermitted} from 'features/layout/CommonPage/panels/TransportPanel/components/common/useIsTestPermitted';

type TestDirectoryProps = {
   directoryPath: string;
   header?: string;
   title?: string;
   disabled?: boolean;
   showDirectoryContents?: boolean;
   directoriesOnly?: boolean;
   selectPath?: (path: string) => void;
};

const TestDirectory = (props: TestDirectoryProps) => {
   const {t} = useTranslation();
   const {
      directoryPath,
      header = t('Directory Test Results'),
      title = t('Test connection'),
      disabled,
      showDirectoryContents = false,
      directoriesOnly = true,
      selectPath,
   } = props;
   const {open, handleOpen, handleClose} = useModal();
   const {request, state, loading, error} = useGetApiCallback<ITestFileResult>();
   const [path, setPath] = React.useState(directoryPath);
   const handleTest = (path?: string) => {
      request({
         url: api.directoryTest,
         options: {
            searchParams: {
               directoryPath: encodeURIComponent(path ?? directoryPath),
               accessType: 'WRITE',
            },
            onSuccess: () => {
               handleOpen();
               setPath(path || directoryPath);
            },
         },
      });
   };
   const {partner, anonymous} = React.useContext(transportDataGetterContext);
   const nonAnonymWithinTransaction = !partner && !anonymous;

   const [selected, setSelected] = React.useState<IDiplomatFile>();

   const isPermitted = useIsTestPermitted();

   return !isPermitted ? null : (
      <>
         <Button
            type="button"
            title={title}
            disabled={disabled || !directoryPath || loading}
            onClick={() => handleTest()}
         >
            {t('Test')}
         </Button>
         <Dialog maxWidth={'md'} open={open} close={handleClose} header={header}>
            <DialogContent>
               {state && state.found && (
                  <>
                     <PathFoundComponent data={state} />
                     {showDirectoryContents && (
                        <DirectoryContentsList
                           data={state.directoryContents}
                           request={handleTest}
                           path={path}
                           selected={selected}
                           setSelected={setSelected}
                           directoriesOnly={directoriesOnly}
                        />
                     )}
                  </>
               )}
               {state && !state.found && <PathNotFoundComponent data={state} />}
               {error && <Typography>{t('Test Failed')}</Typography>}
            </DialogContent>
            <DialogActions>
               {selectPath && !nonAnonymWithinTransaction && (
                  <Button
                     disabled={!selected || (directoriesOnly && !selected?.directory)}
                     title={selected?.path}
                     onClick={() => {
                        selectPath(selected!.path);
                        handleClose();
                     }}
                  >
                     {t('Select')}
                  </Button>
               )}
               <Button onClick={handleClose}>{t('Close')}</Button>
            </DialogActions>
         </Dialog>
      </>
   );
};

type PathFoundComponentProps = {
   data: ITestFileResult;
};

const PathFoundComponent = ({data}: PathFoundComponentProps) => {
   const {t} = useTranslation();
   const {writeAccess, fileName} = data;
   return (
      <Box marginBottom={2}>
         <Typography>
            <Trans t={t}>Directory {{fileName}} found.</Trans>
         </Typography>
         <Typography>
            <Trans>
               Write access {{writeAccess: writeAccess ? 'enabled.' : 'is not enabled.'}}
            </Trans>
         </Typography>
      </Box>
   );
};

type PathNotFoundComponentProps = {
   data: ITestFileResult;
};

const PathNotFoundComponent = ({data}: PathNotFoundComponentProps) => {
   const {t} = useTranslation();
   const {ancestor, fileName, writeAccess} = data;
   return (
      <Box marginBottom={2}>
         <Typography>
            <Trans t={t}>Directory {{fileName}} not found.</Trans>
         </Typography>
         <Typography>
            {ancestor ? (
               <Trans>
                  Ancestor directory {{path: ancestor?.path}} found. Write access{' '}
                  {{writeAccess: writeAccess ? 'enabled.' : 'is not enabled.'}}
               </Trans>
            ) : (
               t('Cannot be created because no ancestor directory found.')
            )}
         </Typography>
      </Box>
   );
};

export default TestDirectory;
